import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { graphql } from "../../../../__generated__/gql";
import { useMutation, useQuery } from "urql";
import { FormProvider, useForm } from "react-hook-form";
import EditableTable from "../components/EditableTable/EditableTable";
import EditableCell from "../components/EditableCell/EditableCell";
import OptionsCell from "../components/OptionsCell/OptionsCell";
import UnsavedChangesModal from "../../../../components/UnsavedChangesModal/UnsavedChangesModal";
import Loader from "../../../../ui/Loader/Loader";
import { useToast } from "../../../../ui/Toast/ToastContext";

const FeaturesQuery = graphql(/* GraphQL */ `
  query features {
    features {
      id
      name
      description
      benefits
      emotiveAppeal
      proofOrEvidence
    }
  }
`);

const CreateFeaturesMutation = graphql(/* GraphQL */ `
  mutation createFeatures($input: [CreateFeatureInput!]!) {
    createFeatures(input: $input) {
      id
      name
      description
      benefits
      emotiveAppeal
      proofOrEvidence
    }
  }
`);

const UpdateFeaturesMutation = graphql(/* GraphQL */ `
  mutation updateFeatures($input: [UpdateFeatureInput!]!) {
    updateFeatures(input: $input) {
      id
      name
      description
      benefits
      emotiveAppeal
      proofOrEvidence
    }
  }
`);

const DeleteFeaturesMutation = graphql(/* GraphQL */ `
  mutation deleteFeatures($ids: [String!]!) {
    deleteFeatures(ids: $ids) {
      id
    }
  }
`);

type FeaturesTableRow = {
  id?: string;
  name?: string | null;
  description?: string | null;
  benefits?: string | null;
  emotiveAppeal?: string | null;
  proofOrEvidence?: string | null;
};

const columnHelper = createColumnHelper<FeaturesTableRow>();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columns: ColumnDef<FeaturesTableRow, any>[] = [
  columnHelper.accessor("name", {
    header: "Name",
    size: 300,
    cell: EditableCell,
    meta: {
      tooltip: "The name of the feature or service",
    },
  }),
  columnHelper.accessor("description", {
    header: "Description",
    minSize: 300,
    cell: EditableCell,
    meta: {
      tooltip: "A detailed overview of the feature or service",
    },
  }),
  columnHelper.accessor("benefits", {
    header: "Benefits",
    minSize: 300,
    cell: EditableCell,
    meta: {
      tooltip: "How this feature or service benefits your audience",
    },
  }),
  columnHelper.accessor("emotiveAppeal", {
    header: "Emotive Appeal",
    minSize: 300,
    cell: EditableCell,
    meta: {
      tooltip: "The language you use to describe this feature or benefit",
    },
  }),
  columnHelper.accessor("proofOrEvidence", {
    header: "Proof or Evidence",
    minSize: 300,
    cell: EditableCell,
    meta: {
      tooltip:
        "Any evidence you have to prove the value of this feature or service. This could be a statistic or a convincing example.",
    },
  }),
  columnHelper.display({
    id: "Options",
    size: 72,
    meta: {
      sticky: true,
    },
    cell: OptionsCell,
  }),
];

type FeaturesTableFormData = {
  rows: FeaturesTableRow[];
};

function FeaturesTable({ data }: { data: FeaturesTableRow[] }) {
  const [, updateFeatures] = useMutation(UpdateFeaturesMutation);
  const [, createFeatures] = useMutation(CreateFeaturesMutation);
  const [, deleteFeatures] = useMutation(DeleteFeaturesMutation);
  const { showToast } = useToast();

  const methods = useForm<FeaturesTableFormData>({
    defaultValues: { rows: data },
  });
  const {
    reset,
    getFieldState,
    formState: { defaultValues },
  } = methods;

  const onSubmit = useCallback(
    async (data: FeaturesTableFormData) => {
      const rowsWithIndex = data.rows.map((row, index) => ({ index, ...row }));
      const featuresToCreate = rowsWithIndex.filter((f) => !f.id);
      const featuresToUpdate = rowsWithIndex.filter(
        (f, index): f is typeof f & { id: string } =>
          !!(f.id && getFieldState(`rows.${index}`).isDirty),
      );
      const featuresToDelete =
        defaultValues?.rows?.filter(
          (f): f is typeof f & { id: string } =>
            !!(f?.id && !data.rows.find((row) => row.id === f.id)),
        ) ?? [];
      const [createdFeatures, updatedFeatures] = await Promise.all([
        featuresToCreate.length
          ? createFeatures({
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              input: featuresToCreate.map(({ index, id, ...f }) => f),
            })
          : null,
        featuresToUpdate.length
          ? updateFeatures({
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              input: featuresToUpdate.map(({ index, ...f }) => f),
            })
          : null,
        featuresToDelete.length
          ? deleteFeatures({
              ids: featuresToDelete.map((f) => f?.id),
            })
          : null,
      ]);

      const newFeatures = data.rows.map((feature, index) => {
        const createdFeatureIndex = featuresToCreate.findIndex(
          (f) => f.index === index,
        );

        if (createdFeatureIndex >= 0) {
          return createdFeatures?.data?.createFeatures[createdFeatureIndex];
        }

        const updatedFeatureIndex = featuresToUpdate.findIndex(
          (f) => f.index === index,
        );

        if (updatedFeatureIndex >= 0) {
          return updatedFeatures?.data?.updateFeatures[updatedFeatureIndex];
        }

        return feature;
      });

      reset({ rows: newFeatures.filter(Boolean) });
      showToast({
        title: "Features & services saved",
        variant: "success",
      });
    },
    [
      createFeatures,
      defaultValues?.rows,
      deleteFeatures,
      getFieldState,
      reset,
      showToast,
      updateFeatures,
    ],
  );

  return (
    <FormProvider {...methods}>
      <UnsavedChangesModal isDirty={methods.formState.isDirty} />
      <EditableTable
        propertyName="rows"
        onSubmit={onSubmit}
        columns={columns}
        title="Features & Services"
        description="Define your features and services"
        resourceName={{
          plural: "features or services",
          singular: "features or service",
        }}
        defaultValue={{
          id: "",
          name: "",
          description: "",
          benefits: "",
          emotiveAppeal: "",
          proofOrEvidence: "",
        }}
        maxRows={45}
      />
    </FormProvider>
  );
}

function Features() {
  const [featuresResponse] = useQuery({
    query: FeaturesQuery,
    requestPolicy: "network-only",
  });

  const data: FeaturesTableRow[] = useMemo(() => {
    if (!featuresResponse.data?.features?.length) {
      return [];
    }

    return featuresResponse.data.features.map((feature) => ({
      id: feature.id,
      name: feature.name,
      description: feature.description,
      benefits: feature.benefits,
      emotiveAppeal: feature.emotiveAppeal,
      proofOrEvidence: feature.proofOrEvidence,
    }));
  }, [featuresResponse]);

  if (featuresResponse.fetching) {
    return (
      <div
        style={{
          padding: "32px 0",
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Loader />
      </div>
    );
  }

  return <FeaturesTable data={data} />;
}

export default Features;
